import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import noop from 'lodash/noop';
import {
  ServicesLookupProps,
} from 'views/EMR/components/ServiceLookup';
import {
  useSalt,
} from 'views/EMR/components/ServiceLookup/hooks';
import isNil from 'lodash/isNil';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  ensureInstanceId,
} from 'views/EMR/components/ServiceLookup/identity';
import uniqBy from 'lodash/uniqBy';
import {
  VisitNoteBillingAssessmentCodeDTO,
} from 'dtos/visitNoteBillingAssessmentCodeDTO';
import type {
  ClaimAuthorizationOption,
} from 'views/EMR/components/Authorizations/utils';

type ServiceProviderProps = ServicesLookupProps & {
  children: ReactNode;
};

type ServiceContextProps = Pick<ServicesLookupProps,
  'id'
  | 'placeholder'
  | 'value'
  | 'normalizeOption'
  | 'onChange'
  | 'favorites'
  | 'refetchFavoriteCode'
  | 'dropdownId'
  | 'type'
  | 'allowDuplicate'
  | 'externalFilter'
  | 'actions'
  | 'saveVisitNote'
  | 'readonly'
  | 'showCustomServiceCodePrice'
  | 'formEnabled'
  | 'assessmentCodes'
  | 'claimAuthorizationOptions'
> & {
  onToggleFavoritesList: () => void;
  showFavorites?: boolean;
  fetchCodeFromVisitNote?: (patientId: number | null, visitNoteId: number | null) => void;
  patientId?: number;
  visitNoteId?: number;
  isSaltLoading?: boolean;
  getKey: (item: VisitNoteServiceCode) => string;
  getInstanceId: (item: VisitNoteServiceCode) => string;
  assessmentCodes: VisitNoteBillingAssessmentCodeDTO[];
  claimAuthorizationOptions: ClaimAuthorizationOption[];
};

export const ServiceContext = createContext<ServiceContextProps>({
  onChange: noop,
  normalizeOption: (option) => ({}),
  favorites: [],
  type: 'CustomCode',
  onToggleFavoritesList: noop,
  dropdownId: '',
  id: '',
  getKey: () => '',
  getInstanceId: () => '',
  assessmentCodes: [],
  claimAuthorizationOptions: [],
  formEnabled: true,
});

export default function ServiceProvider({
  children,
  favorites = [],
  type = 'CustomCode',
  patientId,
  visitNoteId,
  normalizeOption,
  onChange,
  onSalt,
  id,
  placeholder,
  refetchFavoriteCode,
  dropdownId,
  allowDuplicate,
  externalFilter,
  actions,
  saveVisitNote,
  value: selectedServices,
  readonly,
  showCustomServiceCodePrice,
  formEnabled = true,
  assessmentCodes = [],
  claimAuthorizationOptions = [],
}: ServiceProviderProps) {
  const [showFavorites, setShowFavorites] = useState<boolean>(false);

  useEffect(() => {
    if (favorites?.length === 0) {
      setShowFavorites(false);
    }
  }, [favorites]);

  const onToggleFavoritesList = () => {
    setShowFavorites((prev) => !prev);
  };

  const {
    priorData,
    fetchCodeFromVisitNote,
    isSaltLoading,
  } = useSalt(type);

  const keyByType = type === 'CustomCode' ? 'serviceName' : 'code';

  useEffect(() => {
    if (!isNil(priorData) && priorData.length > 0) {
      const updatedServices = [...(selectedServices ?? []), ...priorData];
      onChange?.(type === 'CPTCode'
        ? updatedServices
        : uniqBy(updatedServices, keyByType));
      onSalt?.();
    }
  }, [priorData]);

  const getKey = (option: VisitNoteServiceCode) =>
    (type === 'CustomCode' ? (option?.serviceName ?? '') : (option?.code ?? ''));

  const getInstanceId = (o: VisitNoteServiceCode) =>
    ensureInstanceId(o, getKey(o), allowDuplicate ?? false);

  const value = useMemo(() => ({
    favorites,
    onToggleFavoritesList,
    showFavorites,
    type,
    patientId,
    visitNoteId,
    fetchCodeFromVisitNote,
    priorData,
    normalizeOption,
    onChange,
    value: selectedServices,
    id,
    placeholder,
    refetchFavoriteCode,
    dropdownId,
    getKey,
    getInstanceId,
    allowDuplicate,
    externalFilter,
    actions,
    saveVisitNote,
    isSaltLoading,
    readonly,
    showCustomServiceCodePrice,
    formEnabled,
    assessmentCodes,
    claimAuthorizationOptions,
  }), [
    favorites,
    onToggleFavoritesList,
    showFavorites,
    type,
    patientId,
    visitNoteId,
    fetchCodeFromVisitNote,
    priorData,
    normalizeOption,
    onChange,
    id,
    placeholder,
    refetchFavoriteCode,
    dropdownId,
    getKey,
    getInstanceId,
    allowDuplicate,
    externalFilter,
    actions,
    saveVisitNote,
    isSaltLoading,
    readonly,
    showCustomServiceCodePrice,
    formEnabled,
    assessmentCodes,
    claimAuthorizationOptions,
  ]);

  return (
    <ServiceContext.Provider value={value}>
      {children}
    </ServiceContext.Provider>
  );
}
